/* MadMouse — Plataforma Tuning · Three.js viewer.
   Asset URLs come from window.MM_TUNING_ASSETS (injected by mm-tuning-platform.liquid).
   Ported from interactive_workshop_v2 (Jun 2026): nav arrows, Negro/Morado labels, tutorial. */

import * as THREE from 'three';
import { GLTFLoader }       from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls }    from 'three/addons/controls/OrbitControls.js';
import { DRACOLoader }      from 'three/addons/loaders/DRACOLoader.js';
import { RGBELoader }       from 'three/addons/loaders/RGBELoader.js';

function showError(msg) {
  console.error('[MOUSE3D]', msg);
  const el = document.getElementById('err-msg');
  if (el) el.textContent = msg;
  const panel = document.getElementById('err-panel');
  if (panel) panel.style.display = 'block';
}
window.addEventListener('error', e => showError(e.message + (e.filename ? '\n' + e.filename + ':' + e.lineno : '')));
window.addEventListener('unhandledrejection', e => showError(String(e.reason)));

const ASSETS = window.MM_TUNING_ASSETS;
if (!ASSETS) { showError('MM_TUNING_ASSETS no inyectado'); throw new Error('no assets'); }

const viewport = document.getElementById('viewport');
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace    = THREE.SRGBColorSpace;
renderer.toneMapping         = THREE.LinearToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.shadowMap.enabled   = true;
renderer.shadowMap.type      = THREE.PCFSoftShadowMap;
renderer.setClearColor(0x000000, 0);
viewport.appendChild(renderer.domElement);

const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.001, 50);
camera.position.set(0, 0.08, 0.35);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping   = true;
controls.dampingFactor   = 0.06;
controls.rotateSpeed     = 0.6;
controls.enablePan       = false;
controls.enableZoom      = true;
controls.zoomSpeed       = 0.45;
controls.minDistance     = 0.14;
controls.maxDistance     = 0.85;
controls.minPolarAngle   = Math.PI * 0.08;
controls.maxPolarAngle   = Math.PI * 0.55;
controls.autoRotate      = true;
controls.autoRotateSpeed = 0.7;
controls.target.set(0, 0.03, 0);

// ─── RESIZE / SIZING ─────────────────────────────────────
// Mantiene el drawing buffer sincronizado con el tamaño real del viewport.
// Usar el rect del elemento (no window.inner*) evita un buffer obsoleto/0px
// en el primer paint, que era la causa del bug "hay que hacer zoom para que
// aparezca el modelo" en algunos navegadores.
function onResize() {
  const w = viewport.clientWidth  || window.innerWidth;
  const h = viewport.clientHeight || window.innerHeight;
  if (!w || !h) return;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(w, h, false);
  renderer.render(scene, camera);
}
window.addEventListener('resize', onResize);
// Re-mide cuando el layout del viewport se asienta (fuente, scrollbar, etc.).
if (typeof ResizeObserver !== 'undefined') {
  new ResizeObserver(onResize).observe(viewport);
}

let explodedOn    = false;
let explodedModel = null;
const explodedCache = {};

const VARIANT_KEYS = ['v1', 'v2', 'v3'];

function setNavArrowsDisabled(disabled) {
  ['btn-prev', 'btn-next'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.disabled = disabled;
  });
}

function setVarBtnsDisabled(disabled) {
  setNavArrowsDisabled(disabled);
}

function explodedUrlFor(color) {
  return color === 'purple' ? ASSETS.glb.exploded_purple : ASSETS.glb.exploded;
}

function loadExploded(color, onDone, onError) {
  if (explodedCache[color]) { onDone(explodedCache[color]); return; }
  const url = explodedUrlFor(color);
  gltfLoader.load(url, (loaded) => {
    const box    = new THREE.Box3().setFromObject(loaded.scene);
    const center = box.getCenter(new THREE.Vector3());
    const size   = box.getSize(new THREE.Vector3());
    const s      = 0.21 / Math.max(size.x, size.y, size.z);
    loaded.scene.scale.setScalar(s);
    loaded.scene.userData.baseScale = s;
    loaded.scene.position.set(-center.x * s, -center.y * s, -center.z * s);
    applyMaterials(loaded.scene);
    explodedCache[color] = loaded.scene;
    onDone(loaded.scene);
  }, undefined, onError);
}

function showExplodedModel(model) {
  const wb = new THREE.Box3().setFromObject(model);
  const wc = wb.getCenter(new THREE.Vector3());
  controls.target.copy(wc);
  controls.update();
  explodedModel = model;
  const bs = model.userData.baseScale || model.scale.x;
  model.scale.setScalar(bs * 0.82);
  fadeIn(model, null, 500);
  lerpScalar(model, bs * 0.82, bs, 800);
}

window.toggleExplode = function () {
  if (explodedOn) {
    explodedOn = false;
    document.getElementById('btn-explode').classList.remove('active');
    setVarBtnsDisabled(false);
    const closing = explodedModel;
    const bs = closing.userData.baseScale || closing.scale.x;
    explodedModel = null;
    lerpScalar(closing, bs, bs * 0.82, 500);
    fadeOut(closing, () => {
      controls.target.set(0, 0.03, 0);
      controls.update();
    }, 500);
    fadeIn(active, null, 500);
  } else {
    if (!active) return;
    deactivateCurrent(() => {
      explodedOn = true;
      document.getElementById('btn-explode').classList.add('active');
      setVarBtnsDisabled(true);
      const prev = active;
      loadExploded(currentColor, (model) => {
        fadeOut(prev, null, 500);
        showExplodedModel(model);
      }, (err) => {
        console.error('[MOUSE3D] Error cargando explosionado', err);
        explodedOn = false;
        document.getElementById('btn-explode').classList.remove('active');
        setVarBtnsDisabled(false);
        controls.target.set(0, 0.03, 0);
        controls.update();
        fadeIn(active, null, 350);
      });
    });
  }
};

function updateColorToggle() {
  const isP = currentColor === 'purple';
  document.getElementById('color-toggle').classList.toggle('purple-on', isP);
  document.getElementById('label-negro').classList.toggle('active', !isP);
  document.getElementById('label-morado').classList.toggle('active', isP);
}

window.toggleColor = function () {
  currentColor = currentColor === 'black' ? 'purple' : 'black';
  updateColorToggle();

  if (explodedOn) {
    const prevExploded = explodedModel;
    const bs = prevExploded.userData.baseScale || prevExploded.scale.x;
    loadVariant(current, (model) => { active = model; findDome(model); });
    loadExploded(currentColor, (model) => {
      lerpScalar(prevExploded, bs, bs * 0.82, 400);
      fadeOut(prevExploded, () => { showExplodedModel(model); }, 400);
    }, (err) => { console.error('[MOUSE3D] Error cargando explosionado', err); });
    return;
  }

  const prev = active;
  active = null;
  loadVariant(current, (model) => {
    fadeOut(prev, () => {
      active = model;
      findDome(model);
      if (lightOn) applyDomeEmissive(true);
      fadeIn(model);
    });
  });
};

const pmrem = new THREE.PMREMGenerator(renderer);
pmrem.compileEquirectangularShader();

new RGBELoader().load(
  ASSETS.hdr_studio,
  (hdrTexture) => {
    const envMap = pmrem.fromEquirectangular(hdrTexture).texture;
    scene.environment = envMap;
    hdrTexture.dispose();
    pmrem.dispose();
    // El env map cambia cómo se renderiza el material → fuerza un frame.
    renderer.render(scene, camera);
  },
  undefined,
  (err) => showError('No se pudo cargar luces.hdr\n' + (err && err.message || err))
);

const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
dirLight.position.set(0, 3, 2);
scene.add(dirLight);

const domeLight = new THREE.PointLight(0xFFE87C, 0, 0.08, 2);
scene.add(domeLight);
let domeMesh = null;

function findDome(model) {
  domeMesh = null;
  let difusor = null;
  model.traverse(child => {
    if (!child.isMesh) return;
    if (child.name === 'BREP8_DIFUSOR_1') difusor = child;
  });
  if (difusor) {
    domeMesh = difusor;
    const b = new THREE.Box3().setFromObject(difusor);
    const c = b.getCenter(new THREE.Vector3());
    domeLight.position.set(c.x, c.y - 0.005, c.z);
  } else {
    const b = new THREE.Box3().setFromObject(model);
    const c = b.getCenter(new THREE.Vector3());
    domeLight.position.set(c.x, b.max.y * 0.75, c.z);
  }
}

function applyDomeEmissive(on) {
  if (!domeMesh || !domeMesh.material) return;
  const mats = Array.isArray(domeMesh.material) ? domeMesh.material : [domeMesh.material];
  mats.forEach(m => {
    if (!m.emissive) return;
    m.emissive.setHex(on ? 0xffffaa : 0x000000);
    m.emissiveIntensity = on ? 0.5 : 0;
    m.needsUpdate = true;
  });
}

const draco = new DRACOLoader();
draco.setDecoderPath('https://unpkg.com/three@0.160.0/examples/jsm/libs/draco/');
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(draco);

const VARIANTS = {
  v1: { label: '悪魔 STREET' },
  v2: { label: '雷神 SPEED'  },
  v3: { label: '峠 DRIFT'   }
};
const COLOR_PREFIX = { black: 'negro', purple: 'purple' };

const cache        = {};
let   active       = null;
let   current      = 'v1';
let   currentColor = 'black';
let   lightOn      = false;

function setProgress(pct, label) {
  document.getElementById('loader-bar').style.width   = pct + '%';
  document.getElementById('loader-label').textContent = label;
}

function centerModel(model) {
  const box    = new THREE.Box3().setFromObject(model);
  const size   = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const s      = 0.21 / maxDim;
  model.scale.setScalar(s);
  model.position.x = -center.x * s;
  model.position.z = -center.z * s;
  model.position.y = -box.min.y * s;
}

function applyMaterials(model) {
  model.traverse(child => {
    if (!child.isMesh) return;
    child.castShadow    = true;
    child.receiveShadow = true;
    if (!child.material) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    mats.forEach(m => { m.envMapIntensity = 1.0; m.needsUpdate = true; });
  });
}

function buildFallback() {
  const g = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color: 0x0f0f14, roughness: 0.3, metalness: 0.85 });
  const accentMat = new THREE.MeshStandardMaterial({ color: 0xFFD600, roughness: 0.08, metalness: 1, emissive: 0xFFD600, emissiveIntensity: 0.25 });
  const shell = new THREE.Mesh(new THREE.CapsuleGeometry(0.048, 0.105, 12, 24), bodyMat);
  shell.rotation.z = Math.PI / 2;
  g.add(shell);
  const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.013, 0.013, 0.019, 20), accentMat);
  wheel.rotation.z = Math.PI / 2;
  wheel.position.set(0.012, 0.058, 0);
  g.add(wheel);
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.001, 0.006, 0.092), accentMat);
  stripe.position.set(0.048, -0.003, 0);
  g.add(stripe);
  return g;
}

function loadVariant(v, onDone) {
  const prefix = COLOR_PREFIX[currentColor];
  const key    = currentColor + '_' + v;
  const url    = ASSETS.glb[prefix + '_' + v];

  if (cache[key]) { onDone(cache[key]); return; }

  setProgress(10, prefix + '_' + v);

  gltfLoader.load(url,
    (loaded) => {
      setProgress(90, 'Procesando...');
      const model = loaded.scene;
      centerModel(model);
      applyMaterials(model);
      cache[key] = model;
      setProgress(100, 'Listo');
      onDone(model);
    },
    (xhr) => {
      if (xhr.lengthComputable) {
        const pct = Math.round((xhr.loaded / xhr.total) * 80) + 10;
        setProgress(pct, prefix + '_' + v + ' ' + pct + '%');
      }
    },
    (err) => {
      const msg = 'No se pudo cargar ' + prefix + '_' + v + '\n' + (err && err.message || err);
      showError(msg);
      const fb = buildFallback();
      cache[key] = fb;
      setProgress(100, 'Fallback activo');
      onDone(fb);
    }
  );
}

function fadeOut(model, onDone, duration) {
  duration = duration || 350;
  if (!model) { if (onDone) onDone(); return; }
  const t0 = performance.now();
  (function step(now) {
    const p = Math.min((now - t0) / duration, 1);
    model.traverse(c => {
      if (c.isMesh && c.material) {
        const mats = Array.isArray(c.material) ? c.material : [c.material];
        mats.forEach(m => { m.transparent = true; m.opacity = 1 - p; });
      }
    });
    if (p < 1) requestAnimationFrame(step);
    else { scene.remove(model); if (onDone) onDone(); }
  })(performance.now());
}

function fadeIn(model, onDone, duration) {
  duration = duration || 350;
  model.traverse(c => {
    if (c.isMesh && c.material) {
      const mats = Array.isArray(c.material) ? c.material : [c.material];
      mats.forEach(m => { m.transparent = true; m.opacity = 0; });
    }
  });
  scene.add(model);
  const t0 = performance.now();
  (function step(now) {
    const p = Math.min((now - t0) / duration, 1);
    model.traverse(c => {
      if (c.isMesh && c.material) {
        const mats = Array.isArray(c.material) ? c.material : [c.material];
        mats.forEach(m => { m.opacity = p; });
      }
    });
    if (p < 1) requestAnimationFrame(step);
    else {
      model.traverse(c => {
        if (c.isMesh && c.material) {
          const mats = Array.isArray(c.material) ? c.material : [c.material];
          mats.forEach(m => { m.transparent = false; m.opacity = 1; });
        }
      });
      if (onDone) onDone();
    }
  })(performance.now());
}

function lerpScalar(obj, from, to, duration, onDone) {
  const t0 = performance.now();
  (function step(now) {
    const p = Math.min((now - t0) / duration, 1);
    const e = p < 0.5 ? 2*p*p : -1+(4-2*p)*p;
    obj.scale.setScalar(from + (to - from) * e);
    if (p < 1) requestAnimationFrame(step);
    else { if (onDone) onDone(); }
  })(performance.now());
}

function setGraphicLabel(label, animate) {
  const el = document.getElementById('graphic-name');
  if (animate) {
    el.classList.add('switching');
    setTimeout(() => {
      el.textContent = label;
      el.classList.remove('switching');
    }, 220);
  } else {
    el.textContent = label;
  }
}

// ─── INITIAL LOAD ────────────────────────────────────────
loadVariant('v1', (model) => {
  active = model;
  findDome(model);
  fadeIn(model);
  setGraphicLabel(VARIANTS['v1'].label, false);
  // Asegura tamaño/buffer correctos en cuanto el modelo entra a escena
  // (corrige el bug de "modelo invisible hasta hacer zoom" en algunos navegadores).
  onResize();
  requestAnimationFrame(onResize);
  setTimeout(() => {
    document.getElementById('mm-tuning-loader').classList.add('out');
    onResize();
    // Tutorial desactivado de momento. Reactivable con TUTORIAL_AUTORUN o window.startTutorial().
    if (TUTORIAL_AUTORUN) setTimeout(tutorialInit, 750);
  }, 400);
});

// ─── VARIANT NAVIGATION (prev / next arrows) ─────────────
window.prevVariant = function () {
  if (explodedOn) return;
  const idx = VARIANT_KEYS.indexOf(current);
  const next = VARIANT_KEYS[(idx - 1 + VARIANT_KEYS.length) % VARIANT_KEYS.length];
  switchVariant(next);
};

window.nextVariant = function () {
  if (explodedOn) return;
  const idx = VARIANT_KEYS.indexOf(current);
  const next = VARIANT_KEYS[(idx + 1) % VARIANT_KEYS.length];
  switchVariant(next);
};

window.switchVariant = function (v) {
  if (explodedOn) return;
  if (v === current) return;
  current = v;
  setGraphicLabel(VARIANTS[v].label, true);

  const prev = active;
  active = null;
  loadVariant(v, (model) => {
    fadeOut(prev, () => {
      active = model;
      findDome(model);
      if (lightOn) applyDomeEmissive(true);
      fadeIn(model);
    });
  });
};

const ORIGINAL_EXPOSURE = renderer.toneMappingExposure;
const savedIntensities  = new Map();

function lerpLights(targets, duration) {
  const t0 = performance.now();
  (function step(now) {
    const p = Math.min((now - t0) / duration, 1);
    const e = p < 0.5 ? 2*p*p : -1+(4-2*p)*p;
    targets.forEach(({ light, from, to }) => { light.intensity = from + (to - from) * e; });
    if (p < 1) requestAnimationFrame(step);
  })(performance.now());
}

function applyLightState(on) {
  const lightTypes  = [THREE.AmbientLight, THREE.DirectionalLight, THREE.HemisphereLight, THREE.SpotLight];
  const sceneLights = scene.children.filter(obj => obj !== domeLight && lightTypes.some(T => obj instanceof T));
  const targets = [];
  if (on) {
    sceneLights.forEach(l => { savedIntensities.set(l, l.intensity); targets.push({ light: l, from: l.intensity, to: 0 }); });
    targets.push({ light: domeLight, from: domeLight.intensity, to: 8.0 });
    renderer.toneMappingExposure = 0.2;
  } else {
    sceneLights.forEach(l => { targets.push({ light: l, from: l.intensity, to: savedIntensities.get(l) !== undefined ? savedIntensities.get(l) : l.intensity }); });
    targets.push({ light: domeLight, from: domeLight.intensity, to: 0 });
    renderer.toneMappingExposure = ORIGINAL_EXPOSURE;
  }
  lerpLights(targets, 500);
  document.getElementById('bg-dim').classList.toggle('active', on);
}

function deactivateCurrent(cb) {
  if (lightOn) {
    lightOn = false;
    document.getElementById('btn-light').classList.remove('active');
    applyLightState(false);
    if (active) applyDomeEmissive(false);
    cb();
  } else if (explodedOn) {
    explodedOn = false;
    document.getElementById('btn-explode').classList.remove('active');
    setVarBtnsDisabled(false);
    const closing = explodedModel;
    const bs = closing.userData.baseScale || closing.scale.x;
    explodedModel = null;
    lerpScalar(closing, bs, bs * 0.82, 500);
    fadeOut(closing, () => {
      controls.target.set(0, 0.03, 0);
      controls.update();
      cb();
    }, 500);
    fadeIn(active, null, 500);
  } else {
    cb();
  }
}

window.toggleLight = function () {
  if (lightOn) {
    lightOn = false;
    document.getElementById('btn-light').classList.remove('active');
    applyLightState(false);
    if (active) applyDomeEmissive(false);
  } else {
    deactivateCurrent(() => {
      lightOn = true;
      document.getElementById('btn-light').classList.add('active');
      applyLightState(true);
      if (active) { findDome(active); applyDomeEmissive(true); }
    });
  }
};

function tick() {
  requestAnimationFrame(tick);
  controls.update();
  renderer.render(scene, camera);
}
tick();

const RACE_UI_IDS = ['viewport','vignette','top-bar','graphic-label','left-bar','drag-hint','color-bar','share-bar'];

function shareUrl(fondo, formato) {
  const vNum = current.replace('v', '');
  const colorName = COLOR_PREFIX[currentColor];
  const key = colorName + '_v' + vNum + '_' + fondo + '_' + formato;
  return ASSETS.share[key] || '';
}

function updateShareThumbs() {
  [['container','story'],['container','post'],['japan','story'],['japan','post']].forEach(([fondo, fmt]) => {
    const el = document.getElementById('thumb-' + fondo + '-' + fmt);
    const url = shareUrl(fondo, fmt);
    if (el && url) el.src = url;
  });
}

window.downloadShare = function () {
  updateShareThumbs();
  document.getElementById('share-modal-overlay').classList.add('visible');
};

window.closeShareModal = function () {
  document.getElementById('share-modal-overlay').classList.remove('visible');
};

window.downloadShareFile = function (fondo, formato) {
  const url = shareUrl(fondo, formato);
  if (!url) return;
  const ext = (url.split('?')[0].match(/\.(png|jpg|jpeg|webp)$/i) || ['', 'png'])[1].toLowerCase();
  const a = document.createElement('a');
  a.href = url;
  a.download = 'madmouse_' + fondo + '_' + formato + '.' + ext;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const raceOverlay = document.getElementById('race-overlay');
const raceVideo   = document.getElementById('race-video');
const raceClose   = document.getElementById('race-close');

let hideCloseTimer = null;
raceOverlay.addEventListener('pointermove', () => {
  raceClose.style.opacity = '0.5';
  clearTimeout(hideCloseTimer);
  hideCloseTimer = setTimeout(() => { raceClose.style.opacity = '0'; }, 2500);
});

window.openRaceVideo = function () {
  closeShareModal();
  RACE_UI_IDS.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.classList.add('ui-fadeout');
  });
  setTimeout(() => {
    raceOverlay.classList.add('visible');
    raceVideo.play();
  }, 1000);
};

window.closeRaceVideo = function () {
  raceClose.style.opacity = '0';
  clearTimeout(hideCloseTimer);
  raceVideo.pause();
  raceOverlay.classList.remove('visible');
  setTimeout(() => {
    raceVideo.currentTime = 0;
    RACE_UI_IDS.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.classList.remove('ui-fadeout');
    });
  }, 1000);
};

raceVideo.addEventListener('ended', window.closeRaceVideo);

// ─── TUTORIAL ────────────────────────────────────────────
// Cambiar a true para volver a lanzarlo automáticamente en la primera visita.
const TUTORIAL_AUTORUN = false;

const TUT_STEPS = [
  { targetId: 'viewport',      text: 'ARRASTRA PARA ROTAR EL MOUSE',                    virtual: true, interactive: true, radius: 16 },
  { targetId: 'color-bar',     text: 'CAMBIAR ENTRE EL RATÓN NEGRO Y EL RATÓN MORADO', pad: 14,       radius: 22 },
  { targetId: 'graphic-label', text: 'EXPLORA LOS DIFERENTES DISEÑOS', pad: 14,        radius: 16 },
  { targetId: 'btn-light',     text: 'ENCIENDE LA LUZ INTERIOR',       pad: 10,        radius: 50 },
  { targetId: 'btn-explode',   text: 'VER VISTA EXPLOSIONADA',         pad: 10,        radius: 50 },
  { targetId: 'btn-play',      text: 'HAZ DRIFTEAR EL MOUSE',          pad: 10,        radius: 50 },
  { targetId: 'btn-share',     text: 'COMPARTE EN INSTAGRAM',          pad: 10,        radius: 50 }
];

let tutStep = 0;
let tutFirstRender = true;

function tutorialInit() {
  if (localStorage.getItem('madmouse_tut_done')) return;
  const dotsEl = document.getElementById('tutorial-dots');
  TUT_STEPS.forEach((_, i) => {
    const d = document.createElement('div');
    d.className = 'tut-dot';
    d.dataset.i = i;
    dotsEl.appendChild(d);
  });
  tutFirstRender = true;
  tutorialShow(0);
  document.getElementById('tutorial-wrap').classList.add('active');
}

function tutorialShow(step) {
  tutStep = step;
  const cfg = TUT_STEPS[step];
  const el = document.getElementById(cfg.targetId);
  if (!el) { tutorialEnd(); return; }

  const elRect = el.getBoundingClientRect();
  const pad = cfg.pad || 0;
  let sr;

  if (cfg.virtual) {
    const w = Math.min(window.innerWidth * 0.78, 640);
    const h = Math.min(window.innerHeight * 0.52, 380);
    sr = { left: (window.innerWidth - w) / 2, top: (window.innerHeight - h) / 2, width: w, height: h };
  } else {
    sr = { left: elRect.left - pad, top: elRect.top - pad, width: elRect.width + pad * 2, height: elRect.height + pad * 2 };
  }

  const spotlight = document.getElementById('tutorial-spotlight');
  if (tutFirstRender) {
    spotlight.style.transition = 'none';
    tutFirstRender = false;
  } else {
    spotlight.style.transition = '';
  }
  spotlight.style.left         = sr.left + 'px';
  spotlight.style.top          = sr.top + 'px';
  spotlight.style.width        = sr.width + 'px';
  spotlight.style.height       = sr.height + 'px';
  spotlight.style.borderRadius = (cfg.radius || 12) + 'px';
  if (spotlight.style.transition === 'none') {
    requestAnimationFrame(() => { spotlight.style.transition = ''; });
  }

  document.getElementById('tutorial-wrap').style.pointerEvents = cfg.interactive ? 'none' : '';
  document.getElementById('tutorial-next').style.pointerEvents = 'all';
  document.getElementById('tutorial-skip').style.pointerEvents = 'all';

  const tooltip = document.getElementById('tutorial-tooltip');
  const tooltipW = 260;
  const tooltipH = 90;
  const GAP = 22;
  const cx = sr.left + sr.width / 2;

  let tipTop, tipLeft;
  if (cx < window.innerWidth * 0.28) {
    tipTop  = Math.max(20, sr.top + sr.height / 2 - tooltipH / 2);
    tipLeft = sr.left + sr.width + GAP;
  } else if (cx > window.innerWidth * 0.72) {
    tipTop  = Math.max(20, sr.top + sr.height / 2 - tooltipH / 2);
    tipLeft = sr.left - GAP - tooltipW;
  } else if (window.innerHeight - (sr.top + sr.height) >= tooltipH + GAP) {
    tipTop  = sr.top + sr.height + GAP;
    tipLeft = Math.max(20, Math.min(window.innerWidth - tooltipW - 20, cx - tooltipW / 2));
  } else {
    tipTop  = sr.top - GAP - tooltipH;
    tipLeft = Math.max(20, Math.min(window.innerWidth - tooltipW - 20, cx - tooltipW / 2));
  }

  tooltip.style.top   = tipTop  + 'px';
  tooltip.style.left  = tipLeft + 'px';
  tooltip.style.width = tooltipW + 'px';

  document.getElementById('tutorial-text').textContent = cfg.text;
  document.getElementById('tutorial-next').textContent =
    step === TUT_STEPS.length - 1 ? 'LISTO ✓' : 'SIGUIENTE →';

  document.querySelectorAll('.tut-dot').forEach((d, i) => {
    d.classList.toggle('active', i === step);
  });
}

window.tutorialNext = function () {
  if (tutStep >= TUT_STEPS.length - 1) { tutorialEnd(); return; }
  tutorialShow(tutStep + 1);
};

window.tutorialEnd = function () {
  localStorage.setItem('madmouse_tut_done', '1');
  document.getElementById('tutorial-wrap').classList.remove('active');
};

// Reactivar el tutorial manualmente (p. ej. desde un botón de ayuda o la consola).
window.startTutorial = function () {
  localStorage.removeItem('madmouse_tut_done');
  document.getElementById('tutorial-dots').innerHTML = '';
  tutFirstRender = true;
  tutorialInit();
};
